import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LogoImage } from "@/assets/images";
import {
  DashboardIcon,
  DashboardActiveIcon,
  RewardIcon,
  RewardActiveIcon,
  TieringIcon,
  TieringActiveIcon,
  ProgramsIcon,
  ProgramsActiveIcon,
  CustomerIcon,
  CustomerActiveIcon,
  PointsSettingIcon,
  PointsSettingActiveIcon,
  ApprovalIcon,
  ApprovalActiveIcon,
} from "@/assets/svg";
import { FetchMenusUseCase } from "@/feature/menu/domain/usecase/FetchMenusUseCase";
import type { MenuItem as MenuItemModel } from "@/feature/menu/domain/model";
import { getStoredUserData, type LoginUserData } from "@/feature/auth";

/**
 * Icon mapping from API icon names to local SVG components
 */
const iconMap: Record<string, { default: string; active: string }> = {
  LayoutGrid: { default: DashboardIcon, active: DashboardActiveIcon },
  Gift: { default: RewardIcon, active: RewardActiveIcon },
  Layers: { default: TieringIcon, active: TieringActiveIcon },
  Calendar: { default: ProgramsIcon, active: ProgramsActiveIcon },
  Users: { default: CustomerIcon, active: CustomerActiveIcon },
  Settings: { default: PointsSettingIcon, active: PointsSettingActiveIcon },
  CheckCircle: { default: ApprovalIcon, active: ApprovalActiveIcon },
};

/**
 * Fallback menu items when API fails
 */
const fallbackMenuItems: MenuItemModel[] = [
  { menuId: "dashboard", title: "Dashboard", icon: "LayoutGrid", routePath: "/swarapoint/dashboard", sortOrder: 10 },
  { menuId: "reward-catalog", title: "Reward Catalog", icon: "Gift", routePath: "/swarapoint/reward-catalog", sortOrder: 20 },
  { menuId: "tiering-settings", title: "Tiering Settings", icon: "Layers", routePath: "/swarapoint/tiering-settings", sortOrder: 30 },
  { menuId: "programs", title: "Programs", icon: "Calendar", routePath: "/swarapoint/programs", sortOrder: 40 },
  { menuId: "customer-data", title: "Customer Data", icon: "Users", routePath: "/swarapoint/customer-data", sortOrder: 50 },
  { menuId: "points-settings", title: "Points Settings", icon: "Settings", routePath: "/swarapoint/points-settings", sortOrder: 60 },
  { menuId: "merchant-approval", title: "Merchant Approval", icon: "CheckCircle", routePath: "/swarapoint/merchant-approval", sortOrder: 70 },
];

const MENU_CACHE_KEY = "sidebarMenuCache";
const MENU_CACHE_EXPIRY_KEY = "sidebarMenuCacheExpiry";
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// Default user data when no user is logged in
const defaultUserData: LoginUserData = {
  userId: "",
  fullName: "Guest User",
  email: "guest@email.com",
  role: "guest",
  avatarUrl: "https://i.pravatar.cc/150?img=1",
};

export function MainSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuItems, setMenuItems] = useState<readonly MenuItemModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userData, setUserData] = useState<LoginUserData>(defaultUserData);

  const loadMenusFromCache = useCallback((): readonly MenuItemModel[] | null => {
    try {
      const cached = localStorage.getItem(MENU_CACHE_KEY);
      const expiry = localStorage.getItem(MENU_CACHE_EXPIRY_KEY);
      if (cached && expiry && Date.now() < parseInt(expiry, 10)) {
        return JSON.parse(cached) as MenuItemModel[];
      }
    } catch {
      // Ignore cache errors
    }
    return null;
  }, []);

  const saveMenusToCache = useCallback((menus: readonly MenuItemModel[]) => {
    try {
      localStorage.setItem(MENU_CACHE_KEY, JSON.stringify(menus));
      localStorage.setItem(MENU_CACHE_EXPIRY_KEY, (Date.now() + CACHE_TTL_MS).toString());
    } catch {
      // Ignore cache errors
    }
  }, []);

  const fetchMenus = useCallback(async () => {
    // Try cache first
    const cached = loadMenusFromCache();
    if (cached && cached.length > 0) {
      setMenuItems(cached);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const menus = await FetchMenusUseCase();
      setMenuItems(menus);
      saveMenusToCache(menus);
    } catch (err) {
      console.error("Failed to fetch menus:", err);
      setError(err instanceof Error ? err.message : "Failed to load menu");
      setMenuItems(fallbackMenuItems);
    } finally {
      setIsLoading(false);
    }
  }, [loadMenusFromCache, saveMenusToCache]);

  useEffect(() => {
    void fetchMenus();
  }, [fetchMenus]);

  // Load user data from localStorage
  useEffect(() => {
    const storedUser = getStoredUserData();
    if (storedUser) {
      setUserData(storedUser);
    }
  }, []);

  const getActiveItem = useCallback((): string => {
    const currentPath = location.pathname;
    const activeMenuItem = menuItems.find((item) =>
      item.routePath ? currentPath.startsWith(item.routePath) : false,
    );
    return activeMenuItem?.menuId ?? "dashboard";
  }, [location.pathname, menuItems]);

  const activeItem = getActiveItem();

  const getIcon = (iconName: string, isActive: boolean): string => {
    const icons = iconMap[iconName];
    if (!icons) {
      return isActive ? DashboardActiveIcon : DashboardIcon;
    }
    return isActive ? icons.active : icons.default;
  };

  return (
    <aside className="w-[280px] flex flex-col p-4 relative">
      <div className="flex-1 flex flex-col">
        {/* Logo */}
        <div className="flex items-center gap-3 mb-10">
          <img src={LogoImage} alt="Swarapoint Logo" className="w-full" />
        </div>

        {/* Navigation Menu */}
        <nav className="flex flex-col">
          {isLoading ? (
            <div className="flex flex-col gap-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="h-9 bg-white/10 rounded-lg animate-pulse"
                />
              ))}
            </div>
          ) : error ? (
            <div className="text-red-300 text-sm px-3 py-2">{error}</div>
          ) : (
            menuItems.map((item) => {
              const isActive = activeItem === item.menuId;
              const Icon = getIcon(item.icon, isActive);

              return (
                <button
                  key={item.menuId}
                  type="button"
                  className={`flex items-center gap-2 px-3 py-2 border-none rounded-lg cursor-pointer transition-all duration-200 text-left w-full font-medium text-sm leading-5 focus:outline-none ${
                    isActive
                      ? "bg-white text-[#001A41]"
                      : "bg-transparent hover:bg-white/[0.08] text-white"
                  }`}
                  onClick={() => {
                    if (item.routePath) {
                      navigate(item.routePath);
                    }
                  }}
                >
                  <span className="flex items-center justify-center w-5 h-5 flex-shrink-0">
                    <Icon />
                  </span>
                  <span className="flex-1 leading-[1.4]">{item.title}</span>
                  {item.badge && (
                    <span
                      className={`px-2 py-0.5 text-xs rounded-full ${
                        item.badge.variant === "red"
                          ? "bg-red-500 text-white"
                          : "bg-gray-500 text-white"
                      }`}
                    >
                      {item.badge.value}
                    </span>
                  )}
                </button>
              );
            })
          )}
        </nav>
      </div>

      {/* User Profile Footer */}
      <div className="flex items-center mt-auto gap-3 pt-6 border-t border-white">
        <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 border-2 border-white/20">
          <img
            src={userData.avatarUrl || "https://i.pravatar.cc/150?img=1"}
            alt={userData.fullName}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0 text-left">
          <div className="font-medium text-white whitespace-nowrap overflow-hidden text-ellipsis text-sm leading-5">
            {userData.fullName}
          </div>
          <div className="font-normal text-sm leading-5 tracking-[0.005em] text-[#97a6b1] whitespace-nowrap overflow-hidden text-ellipsis">
            {userData.email}
          </div>
        </div>
      </div>
    </aside>
  );
}
